import React, { useCallback, useEffect, useState } from 'react';
import { useAccessToken, useSubjectLanguageVariants } from '@features/user';
import { Autocomplete, TextField } from '@mui/material';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import debounce from 'lodash/debounce';

interface PatientOption {
  patient_id: string;
  label: string;
}

interface OrgSchedulePatientFilterProps {
  selectedPatientIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export const OrgSchedulePatientFilter: React.FC<OrgSchedulePatientFilterProps> = ({
  selectedPatientIds,
  onSelectionChange,
}) => {
  const { getAccessToken } = useAccessToken();
  const subjectVariants = useSubjectLanguageVariants();
  const { orgSchedule } = useEventTracking();
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Keep selected options in sync when IDs are cleared externally
  useEffect(() => {
    if (selectedPatientIds.length === 0 && selectedOptions.length > 0) {
      setSelectedOptions([]);
    }
  }, [selectedPatientIds, selectedOptions.length]);

  const fetchPatients = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/search?q=${encodeURIComponent(searchTerm)}&page=1&per_page=10`,
          accessToken,
        });
        if (response.ok) {
          const data = response.value as {
            patients: { patient_id: string; first_name?: string; last_name?: string }[];
          };
          const patientOptions: PatientOption[] = data.patients.map((p) => ({
            patient_id: p.patient_id,
            label: `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || 'Unknown',
          }));
          setOptions(patientOptions);
        }
      } catch (error) {
        console.error('Error fetching patients for org filter:', error);
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(debounce(fetchPatients, 300), [fetchPatients]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selectedOptions}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.patient_id === value.patient_id}
      popupIcon={null}
      loadingText="Searching..."
      noOptionsText={
        inputValue.length >= 2
          ? `No ${subjectVariants?.plural ?? 'patients'} found`
          : `Type to search ${subjectVariants?.plural ?? 'patients'}`
      }
      onInputChange={(_event, value, reason) => {
        if (reason === 'input') {
          setInputValue(value);
          debouncedFetch(value);
        } else if (reason === 'clear') {
          setInputValue('');
        }
      }}
      onChange={(_event, newValue) => {
        const prevCount = selectedOptions.length;
        const newCount = newValue.length;
        const action = newCount === 0 ? 'cleared' : newCount > prevCount ? 'added' : 'removed';
        setSelectedOptions(newValue);
        onSelectionChange(newValue.map((o) => o.patient_id));
        orgSchedule.patientFilterChanged({ selected_count: newCount, action });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={`Search ${subjectVariants?.pluralCap ?? 'Patients'}`}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid rgba(0, 13, 77, 0.45)',
              boxShadow:
                '0 2px 4px -2px rgba(0, 0, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.04)',
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: '#535976',
              '& fieldset': { border: 'none' },
              '&:hover': {
                borderColor: '#535976',
                backgroundColor: 'rgba(83, 89, 118, 0.04)',
                boxShadow:
                  '0 2px 4px -2px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
              },
              '&.Mui-focused': {
                borderColor: '#535976',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#535976',
              opacity: 0.7,
            },
          }}
        />
      )}
    />
  );
};
